<template>
  <div class="min-h-screen bg-black text-white p-4 sm:p-6 md:p-8">
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8 border-b-2 border-yellow-500 pb-6">
        <h1 class="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-yellow-500">🏆 PODIO 🏆</h1>
        <p class="text-sm sm:text-base md:text-lg text-yellow-400" v-if="torneoNombre">{{ torneoNombre }}</p>
      </div>

      <!-- Selector de torneo -->
      <div class="mb-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <label class="text-sm text-gray-400 uppercase tracking-wide">Torneo:</label>
        <select
          v-model="torneoSeleccionado"
          @change="cambiarTorneo"
          class="bg-gray-900 border border-yellow-500 text-white rounded-lg px-4 py-2 min-w-[260px]"
        >
          <option v-for="t in torneos" :key="t.id" :value="t.id">{{ t.nombre }}</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="animate-spin text-4xl mb-4">⏳</div>
        <p class="text-gray-400 text-lg">Cargando podio...</p>
      </div>

      <!-- Sin torneo -->
      <div v-else-if="!torneoSeleccionado" class="text-center py-16">
        <p class="text-xl text-gray-400">No hay torneos disponibles</p>
      </div>

      <!-- Categorías -->
      <div v-else-if="categorias.length" class="space-y-8">
        <div
          v-for="cat in categorias"
          :key="cat.categoria_id"
          class="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg border border-yellow-500 border-opacity-30 p-6"
        >
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl sm:text-2xl font-bold text-yellow-500 uppercase tracking-wide">{{ cat.categoria_nombre }}</h2>
            <span
              class="text-xs font-semibold px-3 py-1 rounded-full uppercase"
              :class="estadoClase(cat.estado)"
            >
              {{ estadoTexto(cat.estado) }}
            </span>
          </div>

          <!-- Podio finalizado -->
          <div v-if="cat.estado === 'finalizado'" class="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 items-end justify-items-center">
            <!-- 2do puesto -->
            <PodiumSpot
              :competidor="cat.segundo"
              puesto="2°"
              medalla="🥈"
              borde="border-gray-300"
              pedestal-color="bg-gray-300"
              pedestal-alto="h-16"
            />
            <!-- 1er puesto -->
            <PodiumSpot
              :competidor="cat.primero"
              puesto="1°"
              medalla="🥇"
              borde="border-yellow-400"
              pedestal-color="bg-yellow-400"
              pedestal-alto="h-24"
              destacado
            />
            <!-- 3er puesto -->
            <PodiumSpot
              :competidor="cat.tercero"
              puesto="3°"
              medalla="🥉"
              borde="border-orange-600"
              pedestal-color="bg-orange-600"
              pedestal-alto="h-10"
            />
          </div>

          <!-- Aún sin finalizar -->
          <div v-else class="text-center py-8">
            <p class="text-gray-400">
              {{ cat.estado === 'sin_iniciar' ? 'Llaves aún no generadas' : 'Categoría en curso, todavía no hay podio definitivo' }}
            </p>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-16">
        <p class="text-xl text-gray-400">Este torneo no tiene categorías asignadas</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import PodiumSpot from '../../components/PodiumSpot.vue'

const route = useRoute()
const router = useRouter()
const api = useAPI()

const torneos = ref([])
const torneoSeleccionado = ref(null)
const torneoNombre = ref('')
const categorias = ref([])
const loading = ref(true)

const estadoTexto = (estado) => {
  if (estado === 'finalizado') return 'Finalizado'
  if (estado === 'en_curso') return 'En curso'
  return 'Sin iniciar'
}

const estadoClase = (estado) => {
  if (estado === 'finalizado') return 'bg-green-600 text-white'
  if (estado === 'en_curso') return 'bg-blue-600 text-white'
  return 'bg-gray-700 text-gray-300'
}

const cargarPodio = async (torneoId) => {
  loading.value = true
  try {
    const data = await api.getPodio(torneoId)
    torneoNombre.value = data.torneo_nombre
    categorias.value = data.categorias
  } catch (err) {
    console.error('Error cargando podio:', err)
    categorias.value = []
  } finally {
    loading.value = false
  }
}

const cambiarTorneo = () => {
  if (torneoSeleccionado.value) {
    router.replace({ name: 'Podium', params: { torneoId: torneoSeleccionado.value } })
    cargarPodio(torneoSeleccionado.value)
  }
}

onMounted(async () => {
  loading.value = true
  try {
    torneos.value = await api.getTorneos()

    const idDeRuta = route.params.torneoId ? parseInt(route.params.torneoId) : null
    const torneoInicial = idDeRuta && torneos.value.some(t => t.id === idDeRuta)
      ? idDeRuta
      : (torneos.value[0]?.id ?? null)

    torneoSeleccionado.value = torneoInicial

    if (torneoInicial) {
      await cargarPodio(torneoInicial)
    } else {
      loading.value = false
    }
  } catch (err) {
    console.error('Error cargando torneos:', err)
    loading.value = false
  }
})
</script>
